import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
    FormBuilder,
    FormGroup,
    FormControl,
    FormArray,
    Validators,
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
} from "@angular/forms";
import { QuestionRequiredStatus, QuestionType, SurveyType } from "./loan-survey.interface";
import { catchError, finalize } from "rxjs/operators";
import { of, Subscription } from "rxjs";
import { UntilDestroy } from "@ngneat/until-destroy";
import { Router } from "@angular/router";
import { MatDialogSizeService } from "src/app/shared/services/mat-dialog-size";
import { MatDialog } from "@angular/material/dialog";
import { Lottie } from "src/app/shared/constant/lottie";
import { CommonService } from "../../services/common.service";
import { Location } from "@angular/common";
import { ConfirmationDialogComponent } from "../../dialogs/confirmation-dialog/confirmation-dialog.component";
import { MatSelect } from "@angular/material/select";

@UntilDestroy({ arrayName: "subscriptions" })
@Component({
    selector: "web-loan-survey",
    templateUrl: "./loan-survey.component.html",
    styleUrls: ["./loan-survey.component.scss"],
})
export class LoanSurveyComponent {
    subscriptions: Subscription[] = [];
    surveyForm!: FormGroup;
    questions: any[] = [];
    questionType = QuestionType;
    surveyType = SurveyType;
    surveyMeta: number | null = null;
    surveyResponse: any[] = [];
    questionRequiredStatus = QuestionRequiredStatus;
    @Input() surveySlug!: string;
    @Input() isCancel: boolean = false;
    @Input() isSubmit: boolean = false;
    @Input() pageTitle: string = "";
    @Input() isLoading = false;

    showShimmer = false;

    @Output() submitSurvey = new EventEmitter<any>();

    isVerified = false;

    constructor(
        private fb: FormBuilder,
        private commonService: CommonService,
        private router: Router,
        private dialog: MatDialog,
        public lottie: Lottie,
        private location: Location,
        private matDialogSizeService: MatDialogSizeService
    ) {}

    ngOnInit(): void {
        this.surveyForm = this.fb.group({});
    }

    ngAfterViewInit(): void {
        this.loadSurvey();
    }

    loadSurvey(): void {
        this.showShimmer = true;

        let params: any = {};
        if (this.surveySlug && this.surveySlug.trim() !== "") {
            params.slug = this.surveySlug;
        } else {
            params.surveyType = this.surveyType.DECLINE_LOAN.value;
        }

        this.commonService
            .getSurveyList(params)
            .pipe(
                catchError(() => of({ data: { questions: [] } })),
                finalize(() => (this.showShimmer = false))
            )
            .subscribe((response) => {
                const isConfirmationStatus = response?.data?.confirmation?.confirmationStatus || false;
                if (isConfirmationStatus) {
                    this.subscriptions.push(
                        this.dialog
                            .open(ConfirmationDialogComponent, {
                                width: this.matDialogSizeService.wIII,
                                disableClose: true,
                                data: {
                                    content: {
                                        image: "assets/svg/icons/badge-info-gb.svg",
                                        title: response?.data?.confirmation?.confirmationMessage || "Error...!",
                                        confirmButtonName: "Yes, Decline",
                                        cancelButtonName: "No, Continue With Loan",
                                        html: true,
                                    },
                                },
                            })
                            .afterClosed()
                            .subscribe((res: any) => {
                                if (res?.confirmed == false) {
                                    this.location.back();
                                }
                            })
                    );
                }

                this.surveyMeta = response.data?.id || null;
                this.questions = response.data?.questions || [];
                this.pageTitle = response.data?.surveyTitle || this.pageTitle || "";
                this.initForm();
            });
    }

    initForm(): void {
        this.surveyForm = this.fb.group({});

        this.questions.forEach((q) => {
            const controlKey = q.id.toString();
            const isRequired = q.isSurveyQuestion === this.questionRequiredStatus.REQUIRED.value;

            // ✅ EXISTING checkbox MULTIPLE (DO NOT TOUCH)
            if (q.questionType === this.questionType.MULTIPLE.value) {
                const controls = q.options.map(() => new FormControl(false));
                const formArray = new FormArray(controls);

                if (isRequired) {
                    formArray.setValidators(this.minSelectedCheckboxes(1));
                }

                this.surveyForm.addControl(controlKey, formArray);
                return;
            }

            // ✅ NEW SELECT_MULTIPLE
            if (q.questionType === this.questionType.SELECT_MULTIPLE.value) {
                const validators = isRequired ? [Validators.required] : [];
                this.surveyForm.addControl(controlKey, new FormControl([], validators));
                return;
            }

            // ✅ All other types
            const validators = isRequired ? [Validators.required] : [];
            this.surveyForm.addControl(controlKey, new FormControl("", validators));
        });
    }

    // Custom validator for FormArray (at least `min` checkboxes selected)
    minSelectedCheckboxes(min: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const formArray = control as FormArray;
            const totalSelected = formArray.controls
                .map((c) => c.value)
                .reduce((prev, next) => (next ? prev + 1 : prev), 0);
            return totalSelected >= min ? null : { required: true };
        };
    }

    // Update isFormComplete to respect validators
    isFormComplete(): boolean {
        return this.questions.every((q) => {
            const value = this.surveyForm.get(q.id.toString())?.value;
            if (q.questionType === this.questionType.MULTIPLE.value) {
                return (
                    (Array.isArray(value) && value.some((v) => v)) ||
                    q.isSurveyQuestion !== this.questionRequiredStatus.OPTIONAL.value
                );
            }
            if (q.isSurveyQuestion === this.questionRequiredStatus.REQUIRED.value) {
                return value !== null && value !== undefined && value !== "";
            }
            return true; // optional questions
        });
    }

    getMultiSelectFormArray(questionId: number): FormArray {
        return this.surveyForm.get(questionId.toString()) as FormArray;
    }

    toggleCheckboxValue(questionId: any, index: number): void {
        const control = this.getMultiSelectFormControl(questionId, index);
        if (control) {
            control.setValue(!control.value);
        }
    }

    onSubmit(): void {
        if (this.surveyForm.invalid || !this.surveyMeta) return;

        const formValues = this.surveyForm.value;

        // Transform all questions, including unanswered optional ones
        const transformedData = this.questions.map((q) => {
            const value = formValues[q.id];

            // MULTIPLE SELECT type
            if (q.questionType === this.questionType.MULTIPLE.value) {
                const selectedOptions = q.options
                    .map((option: any, index: number) =>
                        value[index]
                            ? {
                                  questionId: q.id,
                                  optionId: option.id,
                                  answers: option.optionName,
                              }
                            : null
                    )
                    .filter(Boolean);

                // If nothing selected, still push blank entry
                if (selectedOptions.length === 0) {
                    return {
                        questionId: q.id,
                        optionId: 0,
                        answers: "",
                    };
                }

                return selectedOptions; // return array (flatten later)
            }

            // ✅ SELECT MULTIPLE (dropdown)
            if (q.questionType === this.questionType.SELECT_MULTIPLE.value) {
                const selectedValues: string[] = value || [];

                // No selection
                if (!selectedValues.length) {
                    return [
                        {
                            questionId: q.id,
                            optionId: 0,
                            answers: [""],
                        },
                    ];
                }

                // One payload per selected option
                return selectedValues.map((val: string) => {
                    const option = q.options.find((o: any) => o.optionName === val);

                    return {
                        questionId: q.id,
                        optionId: option?.id ?? 0,
                        answers: val,
                    };
                });
            }

            // For RADIO, SELECT, TEXT, etc.
            const answerValue = value || "";
            const selectedOptionId = q.options?.find((o: any) => o.optionName === answerValue)?.id ?? 0;

            return {
                questionId: q.id,
                optionId: selectedOptionId,
                answers: answerValue,
            };
        });

        // Flatten multiple-select questions (which return arrays)
        const finalData = transformedData.flat();
        const allAnswers =
            finalData
                .map((item: any) => (Array.isArray(item.answers) ? item.answers.join(", ") : item.answers))
                .filter(Boolean)
                .join(", ") || "";

        // Prepare final payload
        const payload = {
            surveyId: this.surveyMeta,
            data: finalData,
        };

        // Submit survey
        this.isVerified = true;
        this.commonService.surveySubmit(payload).subscribe({
            next: () => {
                this.isVerified = false;
                this.submitSurvey.emit({
                    event: "success",
                    reason: allAnswers,
                });
            },
            error: () => {
                this.submitSurvey.emit({
                    event: "failed",
                    reason: "Failed to submit survey",
                });
                this.isVerified = false;
            },
        });
    }

    getMultiSelectFormControl(questionId: number, index: number): FormControl {
        return this.getMultiSelectFormArray(questionId).controls[index] as FormControl;
    }

    updateOverlayPosition(select: MatSelect) {
        setTimeout(() => {
            (select as any)._overlayDir?.overlayRef?.updatePosition();
        });
    }
    goBack() {
        this.location.back();
    }
}
